/**
 * `audit` translations, for every locale.
 *
 * THE SCOPE KEYS ARE LOAD-BEARING. `scopeCovers` and `scopeExcludes` are what
 * stop the page from implying an audit trail the application does not keep, and
 * they name the modules explicitly rather than saying "some actions". If the
 * log is ever widened, these three strings change in the same commit — a scope
 * note that has drifted is worse than none, because it is believed.
 */
export const audit = {
  mn: {
    audit: {
      title: 'Цалингийн хандалтын бүртгэл',
      subtitle: 'Цалингийн мэдээллийг хэн үзсэн, хэн өөрчилсөн бэ.',
      loadFailed: 'Хандалтын бүртгэлийг ачаалж чадсангүй.',
      scopeTitle: 'Энэ бүртгэл юуг хамрах вэ',
      scopeCovers:
        'Хамрах зүйл: цалингийн тооцоо үзэх, мөр засварлах, тооцоо баталгаажуулах, ' +
        'хязгаарлагдмал хяналтын самбар, мөн тариф, НД-ын ангилал, цалингийн огнооны өөрчлөлт.',
      scopeExcludes:
        'Хамрахгүй зүйл: борлуулалт, цагийн бүртгэл, чөлөө, ээлжийн хуваарь — эдгээр ' +
        'хаана ч бүртгэгддэггүй. Ажилтны хувийн хэргийн хадгалалт болон эрхийн ' +
        'өөрчлөлт бүртгэгддэг ч энд биш: “Өөрчлөлтийн бүртгэл” хуудсыг үзнэ үү.',
      filterAction: 'Үйлдэл', anyAction: 'Бүх үйлдэл',
      filterPerson: 'Хэн', anyPerson: 'Бүгд',
      from: 'Эхлэх', to: 'Дуусах', clear: 'Цэвэрлэх',
      when: 'Хэзээ', who: 'Хэн', what: 'Юу', run: 'Тооцоо',
      subject: 'Хэнд', detail: 'Дэлгэрэнгүй',
      wholeRun: 'Бүх тооцоо',
      loading: 'Ачаалж байна…', empty: 'Тохирох бичлэг алга',
      logLoadFailed: 'Аудитын бүртгэлийг ачаалж чадсангүй.',
      count: '{{count}} бичлэг', page: '{{page}}-р хуудас',
      previous: 'Өмнөх', next: 'Дараах',
      action: {
        view: 'Үзсэн', adjust: 'Засварласан', issue: 'Баталгаажуулсан',
        rate_change: 'Тариф өөрчилсөн', export: 'Татаж авсан',
        // Defer / undefer, the paid-advance unlock, the tax exemption.
        defer: 'Банкны экспортоос хассан', undefer: 'Шилжүүлгийг цуцалсан',
        unlock: 'Урьдчилгааг нээсэн', exemption: 'Татварын чөлөөлөлт',
        void: 'Хүчингүй болгосон', paid: 'Төлсөн гэж тэмдэглэсэн', unpaid: 'Төлбөрийн тэмдэглэгээг цуцалсан',
      },
      // The administrative log's own page. The action and kind
      // labels are NOT repeated here: they live under `changes.action` and
      // `changes.kind` and are shared, because `/admin/audit` and
      // `/admin/changes` read the same table and two label sets over one
      // vocabulary would drift.
      adminActions: {
        title: 'Удирдлагын үйлдлийн бүртгэл',
        subtitle: 'Бүртгэл, эрх, автоматжуулалтад хэн юу хийсэн бэ.',
        scopeTitle: 'Энэ бүртгэл юуг хамрах вэ',
        scopeCovers:
          'Хамрах зүйл: бүртгэл үүсгэх, өөрчлөх, эрхийн түвшин, урилга, түр эрх, бусдын нэрээр үзэх, автоматжуулалтын дүрэм болон бүх автомат и-мэйлийн сэлгүүр, арга хэмжээний сан, ээлжийн буцаан бүртгэл.',
        scopeExcludes:
          'Хамрахгүй зүйл: цалингийн мэдээлэл үзэх, засах — тэдгээр нь цалингийн ' +
          'хандалтын бүртгэлд байдаг. Хоосон жагсаалт нь энэ хугацаанд ийм үйлдэл ' +
          'БҮРТГЭГДЭЭГҮЙ гэсэн үг.',
        filterAction: 'Үйлдэл', anyAction: 'Бүх үйлдэл',
        filterTarget: 'Юун дээр', anyTarget: 'Бүгд',
        columnKind: 'Төрөл',
        operator: 'Платформын оператор',
        operatorNote: 'Энэ байгууллагын хүн биш — платформын консолоос хийгдсэн.',
      },
      // `GET /api/audit/person/{id}/trail` — the
      // cross-person signature trail, gated the same as every route in this
      // router (`admin`). NOT the record trail under `changes.trail`
      // above: that is one row's `created_by`/`updated_by` stamps, this is
      // the full act history from `person_event`.
      personTrail: {
        title: 'Хүний бүртгэлийн ул мөр',
        subtitle: 'Нэг хүнд хамаарах бүх шийдвэр, зөвшөөрөл, тэмдэглэл — нэг дор.',
        choosePerson: 'Ажилтан сонгох',
        personPrompt: 'Ул мөрийг харахын тулд хүн сонгоно уу.',
        // `{{label}}` carries `before_tracking_label` VERBATIM -- see
        // `usePersonTrail` in `lib/audit.ts`. Never re-worded or replaced.
        loadFailed: 'Хүний мөрийг ачаалж чадсангүй.',
        trackingNote: 'Бүртгэл {{date}}-с хөтлөгдөж эхэлсэн. Түүнээс өмнөх үйлдлүүд: “{{label}}”.',
        columnSurface: 'Хаана', columnAction: 'Үйлдэл', columnStatus: 'Төлөв',
        columnComment: 'Тэмдэглэл', columnActor: 'Хэн',
        noteUnchanged: 'Өөрчлөгдсөн талбар алга',
        field: { work_date: 'Огноо', days: 'Өдөр', hours: 'Цаг', project: 'Төсөл', note: 'Тэмдэглэл' },
        empty: 'Бичлэг алга — {{date}}-с хойш ямар ч үйлдэл бүртгэгдээгүй.',
        surface: {
          purchase_request: 'Худалдан авалтын хүсэлт',
          trip: 'Томилолт',
          leave_application: 'Чөлөөний хүсэлт',
          time_day: 'Цагийн бүртгэл',
          person_history: 'Хувийн хэрэг',
          salary_run: 'Цалингийн тооцоо',
          leave_settlement: 'Амралтын тооцоо',
          contractor_log: 'Гэрээт ажилтны бүртгэл',
          leave_reset: 'Богино хугацааны чөлөө, өвчний чөлөөний жилийн шинэчлэл',
        },
        // THE FULL ENFORCED VOCABULARY IS `PERSON_EVENT_ACTIONS` UNION
        // `LEAVE_TRAIL_ACTIONS` in `backend/trg_digital/models/person_event.py`,
        // surface-keyed by `ck_person_event_action` -- see
        // `schemas/audit.py`'s `PersonTrailEntryOut.action`. A verb missing
        // here renders RAW: `PersonTrail.tsx`'s `actionKey` falls through to the
        // literal database string when the key resolves to nothing. `auditActions.test.ts` pins this
        // map against a hand-copied mirror of the two backend tuples -- see
        // its own comment for why a copy was unavoidable from a frontend test.
        action: {
          approve: 'Баталсан', reject: 'Татгалзсан', comment: 'Тэмдэглэл үлдээсэн',
          override: 'Дарж бичсэн', issue: 'Баталгаажуулсан', edit: 'Засварласан',
          sign: 'Зөвшөөрөл өгсөн', request_cancel: 'Цуцлахыг хүссэн',
          withdraw_cancel: 'Цуцлах хүсэлтээ буцаасан', cancel: 'Цуцалсан',
          admin_edit: 'Админ засварласан', expire: 'Хугацаа дууссан',
          submit: 'Илгээсэн', resubmit: 'Дахин илгээсэн',
          imported_comment: 'Өмнөх тэмдэглэл (зохиогч бүртгэгдээгүй)',
          send: 'Явуулсан гэж тэмдэглэсэн',
        },
      },
      // The trail of ONE record, shown on the record itself.
      // Its `surface`/`action` vocabulary is REUSED from `personTrail` above
      // rather than restated — the same acts read through a different key, so
      // a second copy could only drift.
      documentTrail: {
        // The literal `actor_name` for a system act is 'automatically'. This
        // label REPLACES it in the badge, chosen by `actor_kind` — never by
        // matching that string.
        systemActor: 'Автоматаар',
        onRecordBy: '{{date}} гэхэд бүртгэгдсэн',
      },
      changes: {
        title: 'Өөрчлөлтийн бүртгэл',
        subtitle: 'Юу өөрчлөгдсөн, хэн өөрчилсөн, аль бүртгэлд нотолгоо нь үлдсэн бэ.',
        scopeTitle: 'Дөрвөн эх сурвалж, тус тусдаа',
        scopeNote: 'Өөрчлөлт зөвхөн эдгээр дөрвөн газарт бүртгэгддэг. Бичлэг бүр аль эх сурвалжаас ирснээ хэлнэ — учир нь тэдгээр нь өөр өөр асуултад хариулдаг бөгөөд нэг урсгал болгон нийлүүлбэл энэ ялгаа алдагдана.',
        absence: 'Хоосон жагсаалт нь энэ хугацаанд юу ч БҮРТГЭГДЭЭГҮЙ гэсэн үг — юу ч болоогүй гэсэн үг биш. Борлуулалт, цагийн бүртгэл, чөлөө, ээлжийн хуваарь хаана ч бүртгэгддэггүй.',
        scopeOrg: 'Байгууллагын хэмжээнд',
        scopePerson: 'Нэг хүн',
        choosePerson: 'Ажилтан сонгох',
        personPrompt: 'Бүртгэлийн мөр ба хувийн хэргийн түүхийг харахын тулд хүн сонгоно уу.',
        filterSource: 'Эх сурвалж',
        anySource: 'Бүх эх сурвалж',
        unavailable: 'Харагдахгүй — {{ability}} эрх шаардлагатай',
        filteredOut: 'Эх сурвалжийн шүүлтүүрээр хасагдсан',
        personOnly: 'Зөвхөн нэг хүнээр',
        entriesHere: '{{count}} бичлэг',
        columnSource: 'Эх сурвалж',
        columnSubject: 'Хэнд/юунд',
        unrecorded: 'Бүртгэгдсэн хэрэглэгч алга (импорт эсвэл автомат ажил)',
        unknownActor: 'Тодорхойгүй хүн',
        source: {
          record_trail: 'Бүртгэлийн мөр',
          personnel_record: 'Хувийн хэрэг',
          admin_action: 'Админ',
          payroll_access: 'Цалингийн хандалт',
        },
        sourceNote: {
          record_trail: 'Энэ мөрийг хэн үүсгэж, хэн хамгийн сүүлд өөрчилсөн — нэг мөр, түүх биш.',
          personnel_record: 'Хувийн хэрэг хадгалагдсан эсэх, хэзээ, хэн — агуулгыг нь хэзээ ч харуулахгүй.',
          admin_action: 'Бүртгэл, эрхийн түвшин, урилга, түр эрх, автоматжуулалтын дүрэм, байгууллага.',
          payroll_access: 'Цалингийн мэдээллийг хэн үзсэн, хэн өөрчилсөн.',
        },
        kind: {
          app_user: 'Бүртгэл',
          access_level: 'Хандах эрхийн түвшин',
          automation_rule: 'Автоматжуулалтын дүрэм',
          organization: 'Байгууллага',
          payroll_run: 'Цалингийн тооцоо',
          // Gathered from every `record_admin_action` call site, not from one
          // constants block.
          person: 'Ажилтан',
          activity_expense: 'Арга хэмжээний зардал',
          activity_contribution: 'Арга хэмжээний хандив',
          activity_fund_extra_income: 'Сангийн нэмэлт орлого',
          roster_plan: 'Ээлжийн төлөвлөгөө',
          roster_actual: 'Бодит ээлж',
        },
        trail: {
          heading: 'Бүртгэлийн мөр',
          created: 'Үүсгэсэн',
          updated: 'Сүүлд өөрчилсөн',
          unrecorded: 'Импортоор орсон (хэрэглэгч бүртгэгдээгүй)',
          unknown: 'Тодорхойгүй хүн',
          loadFailed: 'Бүртгэлийн мөрийг ачаалж чадсангүй.',
          unavailable: 'Боломжгүй — hr.view эрх шаардлагатай',
        },
        action: {
          personnel_record: {
            general: 'Хувийн хэрэг хадгалагдсан',
            org_chart: 'Бүтцийн бүртгэл хадгалагдсан',
          },
          admin_action: {
            user_create: 'Бүртгэл үүсгэсэн',
            user_update: 'Бүртгэл өөрчилсөн',
            user_relink_identity: 'Хэрэглэгчийн нэвтрэх бүртгэл дахин холбосон',
            access_level_create: 'Эрхийн түвшин үүсгэсэн',
            access_level_update: 'Эрхийн түвшин өөрчилсөн',
            access_level_delete: 'Эрхийн түвшин устгасан',
            invitation_send: 'Урилга илгээсэн',
            automation_rule_create: 'Автоматжуулалтын дүрэм үүсгэсэн',
            automation_rule_update: 'Автоматжуулалтын дүрэм өөрчилсөн',
            automation_rule_delete: 'Автоматжуулалтын дүрэм устгасан',
            automation_master_switch: 'Бүх автомат и-мэйлийг сэлгэсэн',
            impersonation_start: 'Энэ хүний нэрээр үзсэн',
            elevation_grant: 'Түр эрх олгосон',
            elevation_revoke: 'Түр эрх цуцалсан',
            organization_create: 'Байгууллага үүсгэсэн',
            organization_export_started: 'Компанийн өгөгдлийн экспорт эхэлсэн',
            organization_export_finished: 'Компанийн өгөгдлийн экспорт дууссан',
            organization_deactivate: 'Компанийг унтраасан',
            organization_reactivate: 'Компанийг дахин асаасан',
            administrator_appoint: 'Админ томилсон',
            organization_entra_tenant: 'Microsoft-ын түрээслэгч тохируулсан',
            organization_update: 'Байгууллагын мэдээлэл зассан',
            organization_address_create: 'Байгууллагын хаяг нэмсэн',
            organization_address_update: 'Байгууллагын хаяг зассан',
            organization_address_delete: 'Байгууллагын хаяг устгасан',
            organization_identity_create: 'Гаргагч нэмсэн',
            organization_identity_update: 'Гаргагч зассан',
            employee_self_attach: 'Өөрийн нэвтрэх эрхэд ажилтны бүртгэл холбосон',
            organization_seal_update: 'Гаргагчийн тамга сольсон',
            organization_logo_update: 'Гаргагчийн лого сольсон',
            organization_bank_account_names: 'Дансны англи нэрийг зассан',
            administrator_remove: 'Админ хассан',
            // Seven actions `services/audit.py` has and this block
            // did not, so the change view rendered them as raw strings through
            // the fallback.
            activity_fund_edit_outside_window: 'Хугацаанаас гадуур сангийн бичлэг өөрчилсөн',
            activity_fund_edit_settings: 'Сангийн тохиргоо өөрчилсөн',
            activity_fund_edit_contribution: 'Сангийн хандив өөрчилсөн',
            activity_fund_edit_extra_income: 'Сангийн нэмэлт орлого өөрчилсөн',
            person_erase_pii: 'Хувийн мэдээлэл устгасан',
            roster_plan_backdated: 'Өнгөрсөн өдрийн төлөвлөгөө өөрчилсөн',
            roster_actual_postdated: 'Ирээдүйн бодит ээлж бүртгэсэн',
            roster_actual_backdated: 'Өнгөрсөн өдрийн бодит ээлж өөрчилсөн',
            roster_actual_filed_by_override: 'Томилолтоос гадуур бодит ээлж бүртгэсэн',
            roster_actual_cleared_by_override: 'Томилолтоос гадуур бодит ээлж цуцалсан',
            roster_actual_after_last_day: 'Сүүлийн ажлын өдрөөс хойш бодит ээлж бүртгэсэн',
            payroll_close: 'Цалингийн сарыг хаасан',
            payroll_reopen: 'Цалингийн сарыг дахин нээсэн',
            person_link_company: 'Ажилтныг компанийн мэдээлэлтэй холбосон',
            person_deactivation_outcome: 'Идэвхгүй болгоход эцсийн цалингийн шийдвэр бүртгэсэн',
            person_unlink_company: 'Ажилтныг компанийн мэдээллээс салгасан',
            signature_upload: 'Гарын үсэг оруулсан',
            signature_retire: 'Гарын үсэг хассан',
            leave_override: 'Чөлөөний дүрмийг шалтгаантай давсан',
            signature_switch_off: 'Гарын үсгийг унтраасан',
            signature_switch_on: 'Гарын үсгийг асаасан',
            product_move: 'Бүтээгдэхүүний хэрэглээг шилжүүлсэн',
            product_delete: 'Бүтээгдэхүүн устгасан',
            time_log_transit_zeroed: 'Тээврийн бус мөрийн тээврийн цагийг тэглэсэн',
            auth_password_set: 'Администратор нууц үг тохируулсан',
            auth_password_cleared: 'Администратор нууц үгийг устгасан',
            auth_password_changed: 'Хэрэглэгч өөрийн нууц үгийг сольсон',
            auth_password_reset_by_code: 'Администратор и-мэйлийн кодоор нууц үгээ шинэчилсэн',
            auth_password_locked: 'Нууц үгээр нэвтрэхийг түр түгжсэн',
            auth_e2e_sign_in: 'Хөгжүүлэлтийн тест хэрэглэгч нэвтэрсэн',
            auth_demo_sign_in: 'Демо хэрэглэгч нэвтэрсэн',
            share_link_create: 'Нийтийн харах холбоос үүсгэсэн',
            share_link_revoke: 'Нийтийн харах холбоосыг цуцалсан',
            activity_merge: 'Үйл ажиллагааг өөр үйл ажиллагаатай нэгтгэсэн',
            activity_delete: 'Үйл ажиллагааг устгасан',
            oreas_delete: 'OREAS бүтээгдэхүүн устгасан',
            oreas_value_rename: 'OREAS утгын нэрийг өөрчилсөн',
            client_create: 'Харилцагч нэмсэн',
            client_update: 'Харилцагч өөрчилсөн',
            client_delete: 'Харилцагч устгасан',
            client_merge: 'Харилцагчийг өөр харилцагчтай нэгтгэсэн',
            price_list_create: 'Үнийн жагсаалтад бараа нэмсэн',
            price_list_update: 'Үнийн жагсаалтын барааг өөрчилсөн',
            price_list_deactivate: 'Үнийн жагсаалтын барааг идэвхгүй болгосон',
            bank_account_create: 'Банкны данс нэмсэн',
            bank_account_update: 'Банкны данс өөрчилсөн',
            bank_account_delete: 'Банкны данс устгасан',
            hr_list_columns_update: 'ХН жагсаалтын үндсэн баганыг өөрчилсөн',
            inventory_hse_kit_moved: 'ХАБЭА-ийн хувцас хэрэгслийг Хувцас хэрэгсэл рүү шилжүүлсэн',
            inventory_hse_sizes_moved: 'ХАБЭА-ийн хувцас, гутлын размерыг Хувцас хэрэгсэл рүү шилжүүлсэн',
            inventory_request_created: 'Тоног төхөөрөмжийн хүсэлт гаргасан',
            inventory_request_cancelled: 'Тоног төхөөрөмжийн хүсэлтээ цуцалсан',
            inventory_request_resolved: 'Тоног төхөөрөмжийн хүсэлтэд хариулсан',
            inventory_appointment_granted: 'Агуулахад томилсон',
            inventory_appointment_revoked: 'Агуулахын томилгоог цуцалсан',
            roster_manager_granted: 'Ээлжийн менежер тэмдэглэсэн',
            roster_manager_revoked: 'Ээлжийн менежерийн тэмдэглэгээг авсан',
            roster_staff_appointed: 'Ээлжийн ажилтан томилсон',
            roster_staff_removed: 'Ээлжийн ажилтныг хассан',
            roster_lead_appointed: 'Багийн ахлагч томилсон',
            roster_lead_removed: 'Багийн ахлагчийг хассан',
            roster_manager_appointed: 'Ээлжийн менежерээр тэмдэглэсэн',
            roster_manager_removed: 'Ээлжийн менежерээс хассан',
            roster_project_updated: 'Ээлжийн төслийн тохиргоог өөрчилсөн',
            roster_actual_filed: 'Бодит ээлж бүртгэсэн',
            roster_actual_cleared: 'Бодит ээлж цуцалсан',
            roster_plan_cells_saved: 'Ээлжийн төлөвлөгөөний нүд хадгалсан',
            roster_person_on_break: 'Ээлжийн хуваарьт түр завсарлагад оруулсан',
            roster_person_back: 'Ээлжийн хуваарьт дахин харуулсан',
            roster_city_mark_created: 'Хотод байх өдөр тэмдэглэсэн',
            roster_city_mark_updated: 'Хотод байх өдрийг зассан',
            roster_city_mark_removed: 'Хотод байх өдрийг хассан',
            roster_person_removed: 'Хүнийг ээлжийн хуваариас хассан',
            roster_plan_cells_edited: 'Ээлжийн төлөвлөгөөний нүд зассан',
            roster_plan_published: 'Ээлжийн төлөвлөгөөг нийтэлсэн',
            roster_plan_cleaned: 'Төлөвлөгөөг цэвэрлэсэн',
            roster_staff_edits_reviewed: 'Ажилтны засварыг хянасан гэж тэмдэглэсэн',
          },
          payroll_access: {
            view: 'Цалин үзсэн',
            adjust: 'Мөр засварласан',
            issue: 'Тооцоо баталгаажуулсан',
            rate_change: 'Тариф өөрчилсөн',
            export: 'Татаж авсан',
            defer: 'Цалинг банкны экспортоос хассан',
            undefer: 'Цалингийн шилжүүлгийг цуцалсан',
            unlock: 'Төлсөн урьдчилгааг нээсэн',
            exemption: 'Татвар / НДШ-ийн чөлөөлөлт өөрчилсөн',
          },
        },
      },
    },
  },
  en: {
    audit: {
      title: 'Payroll access log',
      subtitle: 'Who read pay data, and who changed it.',
      loadFailed: 'The access log could not be loaded.',
      scopeTitle: 'What this log covers',
      scopeCovers:
        'Covered: reading a payroll run, adjusting a row, issuing a run, the restricted ' +
        'dashboard, and changes to the rates, contribution bands and pay dates a run computes from.',
      scopeExcludes:
        'Not covered: sales, time logs, leave and the roster — none of those are recorded ' +
        'anywhere. Personnel-record saves and account administration ARE recorded, but not ' +
        'here: see the change record.',
      filterAction: 'Action', anyAction: 'Any action',
      filterPerson: 'Person', anyPerson: 'Anyone',
      from: 'From', to: 'To', clear: 'Clear',
      when: 'When', who: 'Who', what: 'Action', run: 'Run',
      subject: 'Subject', detail: 'Detail',
      wholeRun: 'Whole run',
      loading: 'Loading…', empty: 'No entries match',
      logLoadFailed: 'Could not load the audit log.',
      count: '{{count}} entries', page: 'Page {{page}}',
      previous: 'Previous', next: 'Next',
      action: {
        view: 'Viewed', adjust: 'Adjusted', issue: 'Issued',
        rate_change: 'Rate change', export: 'Exported',
        // Defer / undefer, the paid-advance unlock, the tax exemption.
        defer: 'Deferred', undefer: 'Deferral taken back',
        unlock: 'Advance unlocked', exemption: 'Exemption',
        void: 'Voided', paid: 'Marked paid', unpaid: 'Paid mark taken back',
      },
      adminActions: {
        title: 'Administrative actions',
        subtitle: 'Who did what to accounts, access and automation.',
        scopeTitle: 'What this log covers',
        scopeCovers:
          'Covered: creating and changing accounts, access levels, invitations, ' +
          'elevation, viewing the app as somebody else, automation rules and the ' +
          'master switch over all automated mail, the activity fund, and roster ' +
          'entries recorded out of their day.',
        scopeExcludes:
          'Not covered: reading and adjusting pay data — that is in the payroll ' +
          'access log. An empty list means no action of this kind was RECORDED in ' +
          'this window, not that nothing happened.',
        filterAction: 'Action', anyAction: 'Any action',
        filterTarget: 'Subject kind', anyTarget: 'Any kind',
        columnKind: 'Kind',
        operator: 'Platform operator',
        operatorNote: 'Not a person in this organization — done from the platform console.',
      },
      personTrail: {
        title: 'Person audit trail',
        subtitle: 'Every decision, approval and note recorded about one person, in one place.',
        choosePerson: 'Choose a person',
        personPrompt: 'Choose a person to see their trail.',
        loadFailed: 'Could not load this person’s trail.',
        trackingNote: 'Recorded since {{date}}. Anything before that: "{{label}}".',
        columnSurface: 'Where', columnAction: 'Action', columnStatus: 'Status',
        columnComment: 'Note', columnActor: 'Who',
        noteUnchanged: 'No field changed',
        field: { work_date: 'Date', days: 'Days', hours: 'Hours', project: 'Project', note: 'Note' },
        empty: 'No entries — nothing recorded since {{date}}.',
        surface: {
          purchase_request: 'Purchase request',
          trip: 'Trip',
          leave_application: 'Leave request',
          time_day: 'Time logging',
          person_history: 'Personnel record',
          salary_run: 'Salary run',
          leave_settlement: 'Leave settlement',
          contractor_log: 'Contractor log',
          leave_reset: 'Yearly casual and sick leave reset',
        },
        // See the mn block's comment above `personTrail.action` for the
        // source of truth this map must cover.
        action: {
          approve: 'Approved', reject: 'Rejected', comment: 'Commented',
          override: 'Overrode', issue: 'Issued', edit: 'Edited',
          sign: 'Gave approval', request_cancel: 'Requested cancellation',
          withdraw_cancel: 'Withdrew cancellation', cancel: 'Cancelled',
          admin_edit: 'Edited by an administrator', expire: 'Expired',
          submit: 'Submitted', resubmit: 'Resubmitted',
          imported_comment: 'Earlier comment (author not recorded)',
          send: 'Marked as sent',
        },
      },
      documentTrail: {
        systemActor: 'Automatically',
        onRecordBy: 'on record by {{date}}',
      },
      changes: {
        title: 'Change record',
        subtitle: 'What changed, who changed it, and which record kept the evidence.',
        scopeTitle: 'Four sources, kept apart',
        scopeNote: 'These four are the only places a change is recorded at all. Every entry below says which one it came from, because they answer different questions and merging them into one feed would hide that.',
        absence: 'An empty list means nothing was RECORDED in this window — not that nothing happened. Sales, time logs, leave and the roster are not recorded anywhere.',
        scopeOrg: 'Organization-wide',
        scopePerson: 'One person',
        choosePerson: 'Choose a person',
        personPrompt: 'Choose a person to see their record trail and personnel history.',
        filterSource: 'Source',
        anySource: 'All sources',
        unavailable: 'Not shown — needs {{ability}}',
        filteredOut: 'Hidden by the source filter',
        personOnly: 'Per person only',
        entriesHere: '{{count}} recorded',
        columnSource: 'Source',
        columnSubject: 'Subject',
        unrecorded: 'No actor recorded (import or scheduled job)',
        unknownActor: 'Unknown person',
        source: {
          record_trail: 'Record trail',
          personnel_record: 'Personnel record',
          admin_action: 'Administration',
          payroll_access: 'Payroll access',
        },
        sourceNote: {
          record_trail: 'Who created this row and who last changed it — one row, not a history.',
          personnel_record: 'That a personnel record was saved, when and by whom. Never what it says.',
          admin_action: 'Accounts, access levels, invitations, elevation, automation rules, organizations.',
          payroll_access: 'Who read pay data and who changed it.',
        },
        kind: {
          app_user: 'Account',
          access_level: 'Access level',
          automation_rule: 'Automation rule',
          organization: 'Organization',
          payroll_run: 'Payroll run',
          person: 'Employee',
          activity_expense: 'Activity fund expense',
          activity_contribution: 'Activity fund contribution',
          activity_fund_extra_income: 'Activity fund extra income',
          roster_plan: 'Roster plan',
          roster_actual: 'Roster actual',
        },
        trail: {
          heading: 'Record trail',
          created: 'Created by',
          updated: 'Last changed by',
          unrecorded: 'Imported (no actor recorded)',
          unknown: 'Unknown person',
          loadFailed: 'Could not load the record trail.',
          unavailable: 'Not available — needs hr.view',
        },
        action: {
          personnel_record: {
            general: 'Personnel record saved',
            org_chart: 'Org-chart record saved',
          },
          admin_action: {
            user_create: 'Account created',
            user_update: 'Account changed',
            user_relink_identity: 'Identity re-linked',
            access_level_create: 'Access level created',
            access_level_update: 'Access level changed',
            access_level_delete: 'Access level deleted',
            invitation_send: 'Invitation sent',
            automation_rule_create: 'Automation rule created',
            automation_rule_update: 'Automation rule changed',
            automation_rule_delete: 'Automation rule deleted',
            automation_master_switch: 'All automated mail switched',
            impersonation_start: 'Viewed the app as this person',
            elevation_grant: 'Elevation granted',
            elevation_revoke: 'Elevation revoked',
            organization_create: 'Organization created',
            organization_export_started: 'Company data export started',
            organization_export_finished: 'Company data export finished',
            organization_deactivate: 'Company turned off',
            organization_reactivate: 'Company turned back on',
            administrator_appoint: 'Administrator appointed',
            organization_entra_tenant: 'Microsoft tenant set',
            organization_update: 'Organization edited',
            organization_address_create: 'Organization address added',
            organization_address_update: 'Organization address edited',
            organization_address_delete: 'Organization address deleted',
            organization_identity_create: 'Issuer added',
            organization_identity_update: 'Issuer edited',
            employee_self_attach: 'Attached an employee record to their own login',
            organization_seal_update: 'Issuer seal replaced',
            organization_logo_update: 'Issuer logo replaced',
            organization_bank_account_names: 'Bank account English names edited',
            administrator_remove: 'Administrator removed',
            activity_fund_edit_outside_window: 'Activity fund edited outside the window',
            activity_fund_edit_settings: 'Activity fund settings changed',
            activity_fund_edit_contribution: 'Activity fund contribution changed',
            activity_fund_edit_extra_income: 'Activity fund extra income changed',
            person_erase_pii: 'Personal data erased',
            roster_plan_backdated: 'Roster plan changed for a past day',
            roster_actual_postdated: 'Roster actual recorded for a future day',
            roster_actual_backdated: 'Roster actual changed for a past day',
            roster_actual_filed_by_override: 'Roster actual filed outside own appointment',
            roster_actual_cleared_by_override: 'Roster actual withdrawn outside own appointment',
            roster_actual_after_last_day: 'Roster actual filed after the last working day',
            payroll_close: 'Payroll month closed',
            payroll_reopen: 'Payroll month reopened',
            person_link_company: 'Person linked to a company',
            person_deactivation_outcome: 'Final-pay outcome recorded on deactivation',
            person_unlink_company: 'Person unlinked from a company',
            signature_upload: 'Signature uploaded',
            signature_retire: 'Signature removed',
            leave_override: 'Leave rule overridden',
            signature_switch_off: 'Signature switched off',
            signature_switch_on: 'Signature switched on',
            product_move: 'Product uses moved',
            product_delete: 'Product deleted',
            time_log_transit_zeroed: 'Transit hours zeroed on a non-transit line',
            auth_password_set: 'Password set by an administrator',
            auth_password_cleared: 'Password cleared by an administrator',
            auth_password_changed: 'Password changed by its owner',
            auth_password_reset_by_code: 'Password reset by its owner with an emailed code',
            auth_password_locked: 'Password sign-in locked',
            auth_e2e_sign_in: 'Browser-test user signed in (dev only)',
            auth_demo_sign_in: 'Demo person signed in (buyer demo only)',
            share_link_create: 'Public view link created',
            share_link_revoke: 'Public view link revoked',
            activity_merge: 'Activity merged into another',
            activity_delete: 'Activity deleted',
            oreas_delete: 'OREAS product deleted',
            oreas_value_rename: 'OREAS list value renamed',
            client_create: 'Client added',
            client_update: 'Client changed',
            client_delete: 'Client deleted',
            client_merge: 'Client merged into another',
            price_list_create: 'Price list item added',
            price_list_update: 'Price list item changed',
            price_list_deactivate: 'Price list item deactivated',
            bank_account_create: 'Bank account added',
            bank_account_update: 'Bank account changed',
            bank_account_delete: 'Bank account removed',
            hr_list_columns_update: 'HR list default columns changed',
            inventory_hse_kit_moved: 'HSE kit moved into Gear',
            inventory_hse_sizes_moved: 'HSE clothes and boot sizes moved into Gear',
            inventory_request_created: 'Equipment requested',
            inventory_request_cancelled: 'Equipment request cancelled',
            inventory_request_resolved: 'Equipment request answered',
            inventory_appointment_granted: 'Inventory appointment given',
            inventory_appointment_revoked: 'Inventory appointment removed',
            roster_manager_granted: 'Roster manager ticked',
            roster_manager_revoked: 'Roster manager unticked',
            roster_staff_appointed: 'Roster staff appointed',
            roster_staff_removed: 'Roster staff removed',
            roster_lead_appointed: 'Team lead appointed',
            roster_lead_removed: 'Team lead removed',
            roster_manager_appointed: 'Roster manager ticked',
            roster_manager_removed: 'Roster manager unticked',
            roster_project_updated: 'Roster project set up',
            roster_actual_filed: 'Roster actual filed',
            roster_actual_cleared: 'Roster actual withdrawn',
            roster_plan_cells_saved: 'Roster plan cells saved',
            roster_person_on_break: 'Roster person put on a break',
            roster_person_back: 'Roster person shown again',
            roster_city_mark_created: 'City days marked',
            roster_city_mark_updated: 'City days edited',
            roster_city_mark_removed: 'City days removed',
            roster_person_removed: 'Taken off the roster',
            roster_plan_cells_edited: 'Roster plan cells edited',
            roster_plan_published: 'Roster plan published',
            roster_plan_cleaned: 'Roster plan cleaned',
            roster_staff_edits_reviewed: 'Roster staff edits marked reviewed',
          },
          payroll_access: {
            view: 'Payroll read',
            adjust: 'Row adjusted',
            issue: 'Run finalized',
            rate_change: 'Rate changed',
            export: 'Exported',
            defer: 'Payslip held back from the bank export',
            undefer: 'Payslip deferral taken back',
            unlock: 'Paid advance unlocked',
            exemption: 'Tax / social insurance exemption changed',
          },
        },
      },
    },
  },
  ru: {
    audit: {
      title: 'Журнал доступа к зарплате',
      subtitle: 'Кто просматривал и кто изменял зарплатные данные.',
      loadFailed: 'Не удалось загрузить журнал доступа.',
      scopeTitle: 'Что охватывает этот журнал',
      scopeCovers:
        'Охвачено: просмотр расчёта, правка строки, утверждение расчёта, ограниченная ' +
        'панель, а также изменения ставок, страховых разрядов и дат выплаты.',
      scopeExcludes:
        'Не охвачено: продажи, табель, отпуска и график смен — они не фиксируются нигде. ' +
        'Сохранения личных дел и управление учётными записями фиксируются, но не здесь: ' +
        'см. журнал изменений.',
      filterAction: 'Действие', anyAction: 'Любое действие',
      filterPerson: 'Кто', anyPerson: 'Все',
      from: 'С', to: 'По', clear: 'Сбросить',
      when: 'Когда', who: 'Кто', what: 'Действие', run: 'Расчёт',
      subject: 'Кого', detail: 'Подробности',
      wholeRun: 'Весь расчёт',
      loading: 'Загрузка…', empty: 'Записей не найдено',
      logLoadFailed: 'Не удалось загрузить журнал аудита.',
      count: 'Записей: {{count}}', page: 'Страница {{page}}',
      previous: 'Назад', next: 'Вперёд',
      action: {
        view: 'Просмотр', adjust: 'Правка', issue: 'Утверждение',
        rate_change: 'Изменение ставок', export: 'Выгрузка',
        // Defer / undefer, the paid-advance unlock, the tax exemption.
        defer: 'Перенесено', undefer: 'Перенос отменён',
        unlock: 'Аванс разблокирован', exemption: 'Освобождение',
        void: 'Аннулировано', paid: 'Отмечено выплаченным', unpaid: 'Отметка о выплате снята',
      },
      adminActions: {
        title: 'Административные действия',
        subtitle: 'Кто и что делал с учётными записями, правами и автоматизацией.',
        scopeTitle: 'Что охватывает этот журнал',
        scopeCovers:
          'Охвачено: создание и изменение учётных записей, уровни доступа, ' +
          'приглашения, временные права, просмотр приложения от чужого имени, ' +
          'правила автоматизации и общий выключатель всей автоматической почты, ' +
          'фонд мероприятий и смены, внесённые не своим днём.',
        scopeExcludes:
          'Не охвачено: просмотр и изменение данных о зарплате — это в журнале ' +
          'доступа к зарплате. Пустой список означает, что действия такого рода ' +
          'НЕ ЗАПИСАНЫ в этом окне, а не что ничего не происходило.',
        filterAction: 'Действие', anyAction: 'Любое действие',
        filterTarget: 'Тип объекта', anyTarget: 'Любой тип',
        columnKind: 'Тип',
        operator: 'Оператор платформы',
        operatorNote: 'Не сотрудник этой организации — действие из консоли платформы.',
      },
      personTrail: {
        title: 'След действий по сотруднику',
        subtitle: 'Все решения, согласования и заметки об одном человеке — в одном месте.',
        choosePerson: 'Выберите сотрудника',
        personPrompt: 'Выберите человека, чтобы увидеть его след.',
        loadFailed: 'Не удалось загрузить след сотрудника.',
        trackingNote: 'Запись ведётся с {{date}}. До этого: «{{label}}».',
        columnSurface: 'Где', columnAction: 'Действие', columnStatus: 'Статус',
        columnComment: 'Примечание', columnActor: 'Кто',
        noteUnchanged: 'Поля не изменились',
        field: { work_date: 'Дата', days: 'Дни', hours: 'Часы', project: 'Проект', note: 'Примечание' },
        empty: 'Записей нет — с {{date}} ничего не зафиксировано.',
        surface: {
          purchase_request: 'Заявка на закупку',
          trip: 'Командировка',
          leave_application: 'Заявка на отпуск',
          time_day: 'Табель',
          person_history: 'Личное дело',
          salary_run: 'Расчёт зарплаты',
          leave_settlement: 'Расчёт по отпуску',
          contractor_log: 'Журнал подрядчиков',
          leave_reset: 'Ежегодное обнуление отгулов и больничных',
        },
        // See the mn block's comment above `personTrail.action` for the
        // source of truth this map must cover.
        action: {
          approve: 'Утверждено', reject: 'Отклонено', comment: 'Комментарий',
          override: 'Переопределено', issue: 'Утверждён расчёт', edit: 'Изменено',
          sign: 'Дано согласование', request_cancel: 'Запрошена отмена',
          withdraw_cancel: 'Отмена заявки отозвана', cancel: 'Отменено',
          admin_edit: 'Изменено администратором', expire: 'Просрочено',
          submit: 'Отправлено', resubmit: 'Отправлено повторно',
          imported_comment: 'Ранее оставленный комментарий (автор не записан)',
          send: 'Отмечено как отправленное',
        },
      },
      documentTrail: {
        systemActor: 'Автоматически',
        onRecordBy: 'записано не позднее {{date}}',
      },
      changes: {
        title: 'Журнал изменений',
        subtitle: 'Что изменилось, кто изменил и в какой записи осталось свидетельство.',
        scopeTitle: 'Четыре источника, не смешанные',
        scopeNote: 'Изменения фиксируются только в этих четырёх местах. Каждая запись ниже указывает свой источник: они отвечают на разные вопросы, и объединение их в одну ленту это скрыло бы.',
        absence: 'Пустой список означает, что за этот период ничего не ЗАФИКСИРОВАНО, а не что ничего не происходило. Продажи, табель, отпуска и график смен не фиксируются нигде.',
        scopeOrg: 'По всей организации',
        scopePerson: 'Один человек',
        choosePerson: 'Выберите сотрудника',
        personPrompt: 'Выберите человека, чтобы увидеть след записи и историю личного дела.',
        filterSource: 'Источник',
        anySource: 'Все источники',
        unavailable: 'Не показано — нужно право {{ability}}',
        filteredOut: 'Скрыто фильтром источника',
        personOnly: 'Только по одному человеку',
        entriesHere: 'Записей: {{count}}',
        columnSource: 'Источник',
        columnSubject: 'Объект',
        unrecorded: 'Автор не зафиксирован (импорт или фоновая задача)',
        unknownActor: 'Неизвестный человек',
        source: {
          record_trail: 'След записи',
          personnel_record: 'Личное дело',
          admin_action: 'Администрирование',
          payroll_access: 'Доступ к зарплате',
        },
        sourceNote: {
          record_trail: 'Кто создал эту строку и кто изменил её последним — одна строка, не история.',
          personnel_record: 'Что личное дело было сохранено, когда и кем. Никогда — его содержимое.',
          admin_action: 'Учётные записи, уровни доступа, приглашения, повышение прав, правила, организации.',
          payroll_access: 'Кто просматривал и кто изменял зарплатные данные.',
        },
        kind: {
          app_user: 'Учётная запись',
          access_level: 'Уровень доступа',
          automation_rule: 'Правило автоматизации',
          organization: 'Организация',
          payroll_run: 'Расчёт зарплаты',
          person: 'Сотрудник',
          activity_expense: 'Расход фонда мероприятий',
          activity_contribution: 'Взнос в фонд мероприятий',
          activity_fund_extra_income: 'Дополнительный доход фонда мероприятий',
          roster_plan: 'План смен',
          roster_actual: 'Фактические смены',
        },
        trail: {
          heading: 'След записи',
          created: 'Создал',
          updated: 'Последним изменил',
          unrecorded: 'Импортировано (автор не зафиксирован)',
          unknown: 'Неизвестный человек',
          loadFailed: 'Не удалось загрузить след записи.',
          unavailable: 'Недоступно — нужно право hr.view',
        },
        action: {
          personnel_record: {
            general: 'Личное дело сохранено',
            org_chart: 'Запись оргструктуры сохранена',
          },
          admin_action: {
            user_create: 'Учётная запись создана',
            user_update: 'Учётная запись изменена',
            user_relink_identity: 'Идентификатор перепривязан',
            access_level_create: 'Уровень доступа создан',
            access_level_update: 'Уровень доступа изменён',
            access_level_delete: 'Уровень доступа удалён',
            invitation_send: 'Приглашение отправлено',
            automation_rule_create: 'Правило автоматизации создано',
            automation_rule_update: 'Правило автоматизации изменено',
            automation_rule_delete: 'Правило автоматизации удалено',
            automation_master_switch: 'Вся автоматическая почта переключена',
            impersonation_start: 'Просмотр приложения от имени этого человека',
            elevation_grant: 'Повышение прав выдано',
            elevation_revoke: 'Повышение прав отозвано',
            organization_create: 'Организация создана',
            organization_export_started: 'Начат экспорт данных компании',
            organization_export_finished: 'Завершён экспорт данных компании',
            organization_deactivate: 'Компания отключена',
            organization_reactivate: 'Компания снова включена',
            administrator_appoint: 'Администратор назначен',
            organization_entra_tenant: 'Указан клиент Microsoft',
            organization_update: 'Данные организации изменены',
            organization_address_create: 'Адрес организации добавлен',
            organization_address_update: 'Адрес организации изменён',
            organization_address_delete: 'Адрес организации удалён',
            organization_identity_create: 'Эмитент добавлен',
            organization_identity_update: 'Эмитент изменён',
            employee_self_attach: 'Привязал(а) карточку сотрудника к своей учётной записи',
            organization_seal_update: 'Печать эмитента заменена',
            organization_logo_update: 'Логотип эмитента заменён',
            organization_bank_account_names: 'Английские названия счёта изменены',
            administrator_remove: 'Администратор снят',
            activity_fund_edit_outside_window: 'Фонд мероприятий изменён вне окна',
            activity_fund_edit_settings: 'Настройки фонда мероприятий изменены',
            activity_fund_edit_contribution: 'Взнос в фонд мероприятий изменён',
            activity_fund_edit_extra_income: 'Дополнительный доход фонда мероприятий изменён',
            person_erase_pii: 'Персональные данные удалены',
            roster_plan_backdated: 'План смен изменён задним числом',
            roster_actual_postdated: 'Фактическая смена внесена будущей датой',
            roster_actual_backdated: 'Фактическая смена изменена задним числом',
            roster_actual_filed_by_override: 'Фактическая смена внесена вне своего назначения',
            roster_actual_cleared_by_override: 'Фактическая смена снята вне своего назначения',
            roster_actual_after_last_day: 'Фактическая смена записана после последнего рабочего дня',
            payroll_close: 'Расчётный месяц закрыт',
            payroll_reopen: 'Расчётный месяц снова открыт',
            person_link_company: 'Сотрудник привязан к компании',
            person_deactivation_outcome: 'Записан итог окончательного расчёта при деактивации',
            person_unlink_company: 'Сотрудник отвязан от компании',
            signature_upload: 'Подпись загружена',
            signature_retire: 'Подпись удалена',
            leave_override: 'Правило отпуска обойдено',
            signature_switch_off: 'Подпись отключена',
            signature_switch_on: 'Подпись включена',
            product_move: 'Использование продукта перенесено',
            product_delete: 'Продукт удалён',
            time_log_transit_zeroed: 'Часы в пути обнулены в строке без транзита',
            auth_password_set: 'Пароль задан администратором',
            auth_password_cleared: 'Пароль удалён администратором',
            auth_password_changed: 'Пароль изменён владельцем',
            auth_password_reset_by_code: 'Пароль сброшен владельцем с помощью кода из письма',
            auth_password_locked: 'Вход по паролю заблокирован',
            auth_e2e_sign_in: 'Вход тестового пользователя (только разработка)',
            auth_demo_sign_in: 'Вход демо-пользователя (только демо)',
            share_link_create: 'Создана публичная ссылка для просмотра',
            share_link_revoke: 'Публичная ссылка для просмотра отозвана',
            activity_merge: 'Активность объединена с другой',
            activity_delete: 'Активность удалена',
            oreas_delete: 'Продукт OREAS удалён',
            oreas_value_rename: 'Значение списка OREAS переименовано',
            client_create: 'Клиент добавлен',
            client_update: 'Клиент изменён',
            client_delete: 'Клиент удалён',
            client_merge: 'Клиент объединён с другим',
            price_list_create: 'Позиция прайс-листа добавлена',
            price_list_update: 'Позиция прайс-листа изменена',
            price_list_deactivate: 'Позиция прайс-листа деактивирована',
            bank_account_create: 'Банковский счёт добавлен',
            bank_account_update: 'Банковский счёт изменён',
            bank_account_delete: 'Банковский счёт удалён',
            hr_list_columns_update: 'Изменены столбцы списка HR по умолчанию',
            inventory_hse_kit_moved: 'Спецодежда из ОТ перенесена в Снаряжение',
            inventory_hse_sizes_moved: 'Размеры одежды и обуви из ОТ перенесены в Снаряжение',
            inventory_request_created: 'Запрошено оборудование',
            inventory_request_cancelled: 'Заявка на оборудование отменена',
            inventory_request_resolved: 'Дан ответ на заявку на оборудование',
            inventory_appointment_granted: 'Назначение в инвентаре выдано',
            inventory_appointment_revoked: 'Назначение в инвентаре снято',
            roster_manager_granted: 'Отмечен менеджер графика',
            roster_manager_revoked: 'Снята отметка менеджера графика',
            roster_staff_appointed: 'Назначен сотрудник графика',
            roster_staff_removed: 'Снят сотрудник графика',
            roster_lead_appointed: 'Назначен руководитель группы',
            roster_lead_removed: 'Снят руководитель группы',
            roster_manager_appointed: 'Назначен менеджер графика',
            roster_manager_removed: 'Снят менеджер графика',
            roster_project_updated: 'Изменена настройка проекта графика',
            roster_actual_filed: 'Внесена фактическая смена',
            roster_actual_cleared: 'Отменена фактическая смена',
            roster_plan_cells_saved: 'Сохранены ячейки плана графика',
            roster_person_on_break: 'Сотрудник поставлен на перерыв в графике',
            roster_person_back: 'Сотрудник снова показан в графике',
            roster_city_mark_created: 'Отмечены дни в городе',
            roster_city_mark_updated: 'Изменены дни в городе',
            roster_city_mark_removed: 'Удалены дни в городе',
            roster_person_removed: 'Снят с ростера',
            roster_plan_cells_edited: 'Изменены ячейки плана графика',
            roster_plan_published: 'План графика опубликован',
            roster_plan_cleaned: 'План графика очищен',
            roster_staff_edits_reviewed: 'Правки сотрудников отмечены как проверенные',
          },
          payroll_access: {
            view: 'Просмотр зарплаты',
            adjust: 'Правка строки',
            issue: 'Утверждение расчёта',
            rate_change: 'Изменение ставок',
            export: 'Выгрузка',
            defer: 'Расчётный лист исключён из банковского экспорта',
            undefer: 'Перенос расчётного листа отменён',
            unlock: 'Выплаченный аванс разблокирован',
            exemption: 'Изменено освобождение от налога / соцстрахования',
          },
        },
      },
    },
  },
}
